import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Base } from "../../../framework/base";
import { useTranslation } from "react-i18next";
import WorkTimeSlotCard from "./workTimeSlotCard";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    fetchCurrentVehicleShift,
    fetchGeocodedVehicleShiftSlotAddresses,
    fetchVehicleShiftById,
    fetchVehicleShiftList,
    fetchVehicleTripSegments,
    setHoveredWorkShiftTimeSlotId,
    setSelectedWorkShiftTimeSlotId,
} from "../../../store/monitorViewSlice";
import { positionStreetAddressReq } from "../../../models/geocode/positionTypes";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import VehicleOrdersSection from "./fleetOverviewCard/vehicleOrdersSection";
import Divider from "@mui/material/Divider";
interface MonitorViewVehicleShiftProps {
    transportOrders?: TransportOrderListItemDto[];
}

export const MonitorViewVehicleShift = ({
    transportOrders,
}: MonitorViewVehicleShiftProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );
    const vehicleShift = useAppSelector(
        (state) => state.monitorView.selectedVehicleShift
    );
    const loading = useAppSelector(
        (state) => state.monitorView.vehicleShiftLoading
    );
    const selectedWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.selectedWorkShiftTimeSlotId
    );
    const vehicleShiftList = useAppSelector(
        (state) => state.monitorView.vehicleShiftList
    );

    const vehicleShiftListLoading = useAppSelector(
        (state) => state.monitorView.vehicleShiftListLoading
    );
    const vehicleRoute = useAppSelector(
        (state) => state.monitorView.vehicleRoute
    );
    const slotAddresses = useAppSelector(
        (state) => state.monitorView.vehicleShiftSlotAddresses
    );

    const hasAutoSelected = useRef(false);

    useEffect(() => {
        if (selectedVehicleId) {
            hasAutoSelected.current = false;
            dispatch(fetchCurrentVehicleShift(selectedVehicleId) as any);
            dispatch(fetchVehicleShiftList(selectedVehicleId) as any);
        }
    }, [selectedVehicleId]);

    // Auto-select the first shift from the list when no current shift exists
    useEffect(() => {
        if (
            !vehicleShift &&
            !loading &&
            !vehicleShiftListLoading &&
            vehicleShiftList.length > 0 &&
            !hasAutoSelected.current
        ) {
            hasAutoSelected.current = true;
            dispatch(
                fetchVehicleShiftById(vehicleShiftList[0].vehicleShiftId) as any
            );
        }
    }, [vehicleShift, loading, vehicleShiftListLoading, vehicleShiftList]);

    // Fetch trip segments for the vehicle shift's time range
    useEffect(() => {
        if (vehicleShift?.vehicleId && vehicleShift?.startTime) {
            dispatch(
                fetchVehicleTripSegments({
                    vehicleId: vehicleShift.vehicleId,
                    startDate: vehicleShift.startTime,
                    endDate: vehicleShift.endTime || new Date().toISOString(),
                }) as any
            );
        }
    }, [vehicleShift?.vehicleShiftId]);

    // Geocode work type transition positions when trip segments and shift slots are available
    useEffect(() => {
        const slots = vehicleShift?.workShiftTimeSlots;
        const allPositions = vehicleRoute?.flatMap((seg) => seg.positions);
        if (!slots?.length || !allPositions?.length) return;

        const requests: positionStreetAddressReq[] = slots.map((slot) => {
            const target = new Date(slot.startDate).getTime();
            let closest = allPositions[0];
            let minDiff = Math.abs(
                new Date(closest.timestamp).getTime() - target
            );
            for (let i = 1; i < allPositions.length; i++) {
                const diff = Math.abs(
                    new Date(allPositions[i].timestamp).getTime() - target
                );
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = allPositions[i];
                }
            }
            return {
                id: slot.id,
                lat: closest.latitude,
                lon: closest.longitude,
            };
        });

        if (requests.length > 0) {
            dispatch(fetchGeocodedVehicleShiftSlotAddresses(requests) as any);
        }
    }, [vehicleRoute, vehicleShift?.vehicleShiftId]);

    const currentIndex = useMemo(() => {
        if (!vehicleShift || !vehicleShiftList?.length) return -1;
        return vehicleShiftList.findIndex(
            (s) => s.vehicleShiftId === vehicleShift.vehicleShiftId
        );
    }, [vehicleShift, vehicleShiftList]);

    const hasPrevious = currentIndex > 0;
    const hasNext =
        currentIndex >= 0 && currentIndex < vehicleShiftList.length - 1;

    const navigateTo = useCallback(
        (index: number) => {
            const shift = vehicleShiftList[index];
            if (shift) {
                dispatch(fetchVehicleShiftById(shift.vehicleShiftId) as any);
            }
        },
        [dispatch, vehicleShiftList]
    );

    const shiftTimeLabel = useMemo(() => {
        if (!vehicleShift) return "";
        const start = Base.dateStrToOriginalTimezoneTimeStr(
            vehicleShift.startTime
        );
        const end = vehicleShift.endTime
            ? Base.dateStrToOriginalTimezoneTimeStr(vehicleShift.endTime)
            : "";
        return `${start} - ${end}`;
    }, [vehicleShift]);

    const shiftDateLabel = useMemo(() => {
        if (!vehicleShift) return "";
        return Base.dayjsToDateStr(vehicleShift.startTime);
    }, [vehicleShift]);

    const workShiftTimeSlots = vehicleShift?.workShiftTimeSlots;

    // Orders for this vehicle within the shift's time range
    const shiftOrders = useMemo(() => {
        if (!transportOrders?.length || !vehicleShift?.vehicleId) return [];
        const vid = vehicleShift.vehicleId.toLowerCase();
        const shiftStart = vehicleShift.startTime
            ? Base.dayjsToMinuteAccuracy(vehicleShift.startTime)
            : null;
        const shiftEnd = vehicleShift.endTime
            ? Base.dayjsToMinuteAccuracy(vehicleShift.endTime)
            : Base.dayjsToMinuteAccuracy(); // ongoing shift: use now
        return transportOrders.filter((o) => {
            if (!o.vehicleId || o.vehicleId.toLowerCase() !== vid) return false;
            const start = o.scheduledStartDateTime
                ? Base.dayjsToMinuteAccuracy(o.scheduledStartDateTime)
                : null;
            const end = o.scheduledEndDateTime
                ? Base.dayjsToMinuteAccuracy(o.scheduledEndDateTime)
                : null;
            if (start && shiftEnd && start.isAfter(shiftEnd)) return false;
            if (end && shiftStart && end.isBefore(shiftStart)) return false;
            return true;
        });
    }, [
        transportOrders,
        vehicleShift?.vehicleId,
        vehicleShift?.startTime,
        vehicleShift?.endTime,
    ]);

    if (loading || vehicleShiftListLoading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    if (!vehicleShift && vehicleShiftList.length === 0) {
        return (
            <Alert variant="standard" severity="info">
                {t("monitorView.sidebar.overview.noRows")}
            </Alert>
        );
    }

    return (
        <>
            <Box display="flex" alignItems="center" sx={{ m: 0.5 }}>
                <Box
                    sx={{
                        flex: 1,
                        cursor: "pointer",
                        "&:hover": { opacity: 0.7 },
                        px: 1,
                        py: 0.5,
                    }}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    title={t("monitorView.selectVehicleShift")}
                >
                    <Typography variant="body2" fontWeight={600}>
                        {shiftDateLabel} {shiftTimeLabel}
                    </Typography>
                </Box>
                <IconButton
                    disabled={!hasNext}
                    onClick={() => navigateTo(currentIndex + 1)}
                >
                    <ArrowBack />
                </IconButton>
                <IconButton
                    disabled={!hasPrevious}
                    onClick={() => navigateTo(currentIndex - 1)}
                >
                    <ArrowForward />
                </IconButton>
            </Box>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                slotProps={{
                    paper: {
                        style: { maxHeight: 300 },
                    },
                }}
            >
                {vehicleShiftList.map((shift, index) => (
                    <MenuItem
                        key={shift.vehicleShiftId}
                        selected={index === currentIndex}
                        onClick={() => {
                            navigateTo(index);
                            setMenuAnchor(null);
                        }}
                    >
                        <Typography variant="body2">
                            {Base.dayjsToDateStr(shift.startTime)}{" "}
                            {Base.dateStrToOriginalTimezoneTimeStr(
                                shift.startTime
                            )}
                            {" - "}
                            {shift.endTime
                                ? `${Base.dayjsToDateStr(shift.endTime)} ${Base.dateStrToOriginalTimezoneTimeStr(shift.endTime)}`
                                : ""}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
            {shiftOrders.length > 0 && (
                <Box sx={{ px: 0.5, pt: 0.5 }}>
                    <VehicleOrdersSection orders={shiftOrders} />
                    <Divider sx={{ mt: 0.5 }} />
                </Box>
            )}
            <Box sx={{ px: 0.5, pt: 0.5 }}>
                {workShiftTimeSlots
                    ?.slice()
                    .sort((a, b) => Base.dayjsCompare(a.startDate, b.startDate))
                    .map((s) => (
                        <WorkTimeSlotCard
                            key={s.id}
                            slot={s}
                            isSelected={s.id === selectedWorkShiftTimeSlotId}
                            onSelect={(id) =>
                                dispatch(setSelectedWorkShiftTimeSlotId(id))
                            }
                            onHover={(id) =>
                                dispatch(setHoveredWorkShiftTimeSlotId(id))
                            }
                            address={slotAddresses[s.id]}
                        />
                    ))}
            </Box>
        </>
    );
};
